import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { useAuth } from '@/App';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, Phone, ArrowLeft } from 'lucide-react';

const FAVICON_URL = "https://customer-assets.emergentagent.com/job_multi-watt-hub/artifacts/q9v3nz6d_favicon-riding-watt.png";

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || null;
  const { user, login } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPhone, setRegPhone] = useState('');

  if (user) {
    const dest = redirectTo || (['super_admin', 'franchise_admin'].includes(user.role) ? '/admin' : '/dashboard');
    navigate(dest);
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authAPI.login({ email: loginEmail, password: loginPassword });
      login(res.data.token, res.data.user);
      toast.success(t('common.success'));
      const dest = redirectTo || (['super_admin', 'franchise_admin'].includes(res.data.user.role) ? '/admin' : '/dashboard');
      navigate(dest);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.error_login'));
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authAPI.register({
        email: regEmail, password: regPassword, name: regName, phone: regPhone
      });
      login(res.data.token, res.data.user);
      toast.success(t('auth.success_register'));
      navigate(redirectTo || '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return toast.error('Entrez votre email');
    setLoading(true);
    try {
      await authAPI.forgotPassword({ email: forgotEmail, origin_url: window.location.origin });
      setForgotSent(true);
      toast.success('Email envoyé !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#0A0A0A]" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={FAVICON_URL} alt="Riding Watt" className="w-14 h-14 rounded-2xl mx-auto mb-4 shadow-[0_0_30px_rgba(0,255,0,0.3)]" />
          <h1 className="font-heading text-2xl font-bold text-white">
            RIDING<span className="text-[#00FF00]">WATT</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t('booking.login_required')}</p>
        </div>

        <div className="glass-card rounded-2xl p-8 shadow-float">
          {showForgot ? (
            <div className="space-y-4">
              <button onClick={() => { setShowForgot(false); setForgotSent(false); }} className="flex items-center gap-2 text-gray-400 hover:text-white transition text-sm" data-testid="back-to-login">
                <ArrowLeft className="w-4 h-4" /> Retour à la connexion
              </button>
              <h2 className="text-white text-lg font-bold">Mot de passe oublié</h2>
              {forgotSent ? (
                <div className="space-y-3">
                  <div className="bg-[#00FF00]/10 border border-[#00FF00]/30 rounded-lg p-4">
                    <p className="text-[#00FF00] font-medium">Email envoyé !</p>
                    <p className="text-gray-400 text-sm mt-1">Si un compte existe avec l'adresse <strong className="text-white">{forgotEmail}</strong>, vous recevrez un lien de réinitialisation. Vérifiez aussi vos spams.</p>
                  </div>
                  <Button onClick={() => { setShowForgot(false); setForgotSent(false); }} className="w-full bg-[#1A1A1A] border border-[#2A2A2A] text-white hover:bg-[#2A2A2A]">
                    Retour à la connexion
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-gray-400 text-sm">Entrez votre adresse email. Vous recevrez un lien pour réinitialiser votre mot de passe.</p>
                  <div>
                    <Label className="text-sm font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" /> Email
                    </Label>
                    <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="votre@email.com" required
                      className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                      data-testid="forgot-email" />
                  </div>
                  <Button type="submit" disabled={loading}
                    className="w-full btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] shadow-[0_0_20px_rgba(0,255,0,0.3)] py-6 font-bold"
                    data-testid="forgot-submit">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer le lien'}
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#1A1A1A]">
                <TabsTrigger value="login" className="data-[state=active]:bg-[#00FF00] data-[state=active]:text-black" data-testid="tab-login">{t('auth.login')}</TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-[#00FF00] data-[state=active]:text-black" data-testid="tab-register">{t('auth.register')}</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" /> {t('auth.email')}
                    </Label>
                    <Input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="votre@email.com" required
                      className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                      data-testid="login-email" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" /> {t('auth.password')}
                    </Label>
                    <Input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="********" required
                      className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                      data-testid="login-password" />
                  </div>
                  <button type="button" onClick={() => setShowForgot(true)}
                    className="text-[#00FF00] text-sm hover:underline" data-testid="forgot-password-link">
                    Mot de passe oublié ?
                  </button>
                  <Button type="submit" disabled={loading}
                    className="w-full btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] shadow-[0_0_20px_rgba(0,255,0,0.3)] py-6 font-bold"
                    data-testid="login-submit-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth.submit_login')}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5" /> {t('auth.name')}
                    </Label>
                    <Input value={regName} onChange={(e) => setRegName(e.target.value)}
                      placeholder="Jean Dupont" required
                      className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                      data-testid="register-name" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" /> {t('auth.email')}
                    </Label>
                    <Input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)}
                      placeholder="votre@email.com" required
                      className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                      data-testid="register-email" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" /> {t('auth.phone')}
                    </Label>
                    <Input value={regPhone} onChange={(e) => setRegPhone(e.target.value)}
                      placeholder="+33 6 XX XX XX XX"
                      className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                      data-testid="register-phone" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" /> {t('auth.password')}
                    </Label>
                    <Input type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)}
                      placeholder="Min. 6 caracteres" required
                      className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                      data-testid="register-password" />
                  </div>
                  <Button type="submit" disabled={loading}
                    className="w-full btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] shadow-[0_0_20px_rgba(0,255,0,0.3)] py-6 font-bold"
                    data-testid="register-submit-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth.submit_register')}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
    </div>
  );
}
